<script setup lang="ts">
/** Section « Actions d'accompagnement » — journal du travail réalisé + acteurs. */
import { computed, ref } from 'vue'
import { useToast } from 'vue-toast-notification'
import {
  ClipboardDocumentCheckIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
} from '@heroicons/vue/24/outline'
import type {
  AccompAction,
  AccompActionActor,
  AccompActionInput,
  AccompRequest,
} from '@/v2/accompagnement/api/accompagnementTypes'
import {
  ACCOMP_ACTION_TYPE_LABEL,
  ACCOMP_ACTOR_KIND_LABEL,
} from '@/v2/accompagnement/constants/accompagnementConstants'
import { formatDateShort } from '@/v2/accompagnement/helpers/accompagnementFormat'
import { formatMinutes, totalTimeSpentMinutes } from '@/v2/accompagnement/helpers/accompagnementDelays'
import { canDeleteAction, canLogAction } from '@/v2/accompagnement/helpers/accompagnementPermissions'
import { useAccompagnementStore } from '@/v2/accompagnement/store/useAccompagnementStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import AccompActionDialog from '@/v2/accompagnement/components/AccompActionDialog.vue'

const props = defineProps<{ request: AccompRequest }>()

const store = useAccompagnementStore()
const perms = useBoostCorePermissionStore()
const toast = useToast()

const dialogOpen = ref(false)
const canLog = computed(() => canLogAction(perms))

const sorted = computed(() =>
  [...props.request.actions].sort(
    (a, b) => Date.parse(b.date) - Date.parse(a.date) || Date.parse(b.createdAt) - Date.parse(a.createdAt),
  ),
)

/** Temps total passé sur la demande (somme des minutes des actions). */
const totalMinutes = computed(() => totalTimeSpentMinutes(props.request))

function actorName(a: AccompActionActor): string {
  return `${a.firstName} ${a.lastName}`.trim() || a.email || ACCOMP_ACTOR_KIND_LABEL[a.kind]
}

async function onConfirmed(input: AccompActionInput) {
  const res = await store.addAction(props.request.id, input)
  if (res) {
    toast.success('Action ajoutée.')
    dialogOpen.value = false
  } else {
    toast.error(store.actionError ?? "Échec de l'ajout de l'action.")
  }
}

async function remove(action: AccompAction) {
  const res = await store.deleteAction(props.request.id, action.id)
  if (res) toast.success('Action supprimée.')
  else toast.error(store.actionError ?? 'Échec de la suppression.')
}
</script>

<template>
  <section class="card border border-base-300 bg-base-100 shadow-sm">
    <div class="card-body gap-4 p-4 md:p-6">
      <div class="flex items-center justify-between gap-2">
        <h2 class="card-title flex items-center gap-2 text-lg font-semibold">
          <ClipboardDocumentCheckIcon class="h-5 w-5 text-primary" aria-hidden="true" />
          Actions d'accompagnement
        </h2>
        <button v-if="canLog" type="button" class="btn btn-outline btn-sm gap-1" @click="dialogOpen = true">
          <PlusIcon class="h-4 w-4" aria-hidden="true" />
          Ajouter une action
        </button>
      </div>

      <p v-if="totalMinutes > 0" class="flex items-center gap-1.5 text-sm text-base-content/80">
        <ClockIcon class="h-4 w-4 text-primary" aria-hidden="true" />
        Temps passé total :
        <span class="font-semibold tabular-nums">{{ formatMinutes(totalMinutes) }}</span>
      </p>

      <p v-if="!sorted.length" class="text-base-content/70">
        Aucune action enregistrée pour l'instant.
      </p>

      <ul v-else role="list" class="space-y-3">
        <li v-for="a in sorted" :key="a.id" class="rounded-lg border border-base-200 bg-base-200/40 p-3">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex flex-wrap items-center gap-2">
              <span class="badge badge-secondary badge-sm">{{ ACCOMP_ACTION_TYPE_LABEL[a.type] }}</span>
              <span class="text-sm tabular-nums text-base-content/70">{{ formatDateShort(a.date) }}</span>
              <span
                v-if="a.timeSpentMinutes"
                class="inline-flex items-center gap-1 text-sm tabular-nums text-base-content/70"
              >
                <ClockIcon class="h-3.5 w-3.5" aria-hidden="true" />
                {{ formatMinutes(a.timeSpentMinutes) }}
              </span>
            </div>
            <button
              v-if="canDeleteAction(a, perms, store.currentUserUuid)"
              type="button"
              class="btn btn-ghost btn-xs gap-1 text-error"
              :disabled="store.actionLoading"
              @click="remove(a)"
            >
              <TrashIcon class="h-4 w-4" aria-hidden="true" />
              <span class="sr-only sm:not-sr-only">Supprimer</span>
            </button>
          </div>

          <p v-if="a.note" class="mt-1 whitespace-pre-line text-base-content/80">{{ a.note }}</p>

          <div v-if="a.actors.length" class="mt-2 flex flex-wrap gap-1.5">
            <span
              v-for="(actor, i) in a.actors"
              :key="i"
              class="badge badge-ghost gap-1"
              :title="actor.email || actor.phone || ''"
            >
              <UserIcon class="h-3.5 w-3.5" aria-hidden="true" />
              {{ actorName(actor) }}
              <span class="opacity-60">· {{ ACCOMP_ACTOR_KIND_LABEL[actor.kind] }}</span>
            </span>
          </div>

          <p class="mt-2 text-xs text-base-content/60">
            Ajoutée par {{ a.createdByName }}
          </p>
        </li>
      </ul>

      <AccompActionDialog
        :open="dialogOpen"
        :loading="store.actionLoading"
        @close="dialogOpen = false"
        @confirmed="onConfirmed"
      />
    </div>
  </section>
</template>
